/**
 * DraftCopilot — the conversational + history-aware editing surface for
 * one draft. Replaces the form-based "Refine with AI" panel.
 *
 *   • Chat tab     — message thread with the AI, each AI turn can
 *                    produce a candidate artifact (hook / body / cta /
 *                    hashtags) the editor can Accept (applied via the
 *                    parent's onAcceptArtifact prop) or Discard.
 *   • History tab  — auto-versioned snapshots; click any to preview,
 *                    one-click Restore (also auto-versioned).
 *   • Context tab  — transparency panel: what the AI sees on each turn.
 *
 * All three tabs share the SAME draft + same revision log, so an Accept
 * on the chat tab immediately appears in History.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { History, MessageSquare, Info, Send, Pin, RotateCcw, X, Check } from "lucide-react";
import { MicButton } from "../MicButton";

const API = process.env.REACT_APP_BACKEND_URL;

/* ───── Small util — character-level diff for the candidate preview ─── */
function diffLines(oldText, newText) {
  // Returns an array of { kind: 'same'|'add'|'rem', text } per line.
  const oldLines = (oldText || "").split("\n");
  const newLines = (newText || "").split("\n");
  // LCS-based — small enough for post-length blocks.
  const m = oldLines.length, n = newLines.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = oldLines[i] === newLines[j]
        ? dp[i + 1][j + 1] + 1
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (oldLines[i] === newLines[j]) { out.push({ kind: "same", text: oldLines[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ kind: "rem", text: oldLines[i] }); i++; }
    else { out.push({ kind: "add", text: newLines[j] }); j++; }
  }
  while (i < m) { out.push({ kind: "rem", text: oldLines[i++] }); }
  while (j < n) { out.push({ kind: "add", text: newLines[j++] }); }
  return out;
}

function DiffBlock({ label, oldText, newText, testid }) {
  const lines = diffLines(oldText || "", newText || "");
  return (
    <div data-testid={testid}>
      <div className="font-mono uppercase mb-1"
           style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                    color: "rgba(12,42,64,0.55)", fontWeight: 700 }}>
        {label}
      </div>
      <div style={{
        background: "#FCFCFA",
        border: "1px solid rgba(12,42,64,0.10)",
        borderRadius: 8, padding: "10px 12px",
        fontSize: "0.85rem", lineHeight: 1.55, color: "#0C2A40",
        whiteSpace: "pre-wrap", fontFamily: "ui-sans-serif, system-ui",
      }}>
        {lines.length === 0 && <span style={{ opacity: 0.5 }}>(empty)</span>}
        {lines.map((l, idx) => {
          if (l.kind === "same") return <div key={idx}>{l.text || "\u00A0"}</div>;
          if (l.kind === "add") return (
            <div key={idx} style={{ background: "rgba(31,182,193,0.16)", borderLeft: "3px solid #1FB6C1", padding: "1px 8px", marginLeft: -8 }}>
              {l.text || "\u00A0"}
            </div>
          );
          return (
            <div key={idx} style={{ background: "rgba(184,92,56,0.10)", borderLeft: "3px solid #B85C38", padding: "1px 8px", marginLeft: -8, textDecoration: "line-through", opacity: 0.7 }}>
              {l.text || "\u00A0"}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
export default function DraftCopilot({
  draft,
  apiToken,
  // Live editor state (parent owns these so the chat works against
  // unsaved changes, and Accept can write through the parent's PATCH).
  hook, body, cta, tags,
  setHook, setBody, setCta, setTags,
  // Called after a successful PATCH so the parent can refresh.
  onApplied,
}) {
  const [tab, setTab] = useState("chat"); // 'chat' | 'history'
  const [contextOpen, setContextOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [candidate, setCandidate] = useState(null); // pending artifact awaiting accept/discard
  const [pinned, setPinned] = useState([]);
  const [pinInput, setPinInput] = useState("");

  const [revisions, setRevisions] = useState([]);
  const [previewRev, setPreviewRev] = useState(null);

  const [contextDoc, setContextDoc] = useState(null);

  const json = useMemo(() => ({
    Authorization: `Bearer ${apiToken}`,
    "Content-Type": "application/json",
  }), [apiToken]);

  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, candidate, sending]);

  /* ─── Chat send ───────────────────────────────────────────────── */
  const send = async (overrideText) => {
    const text = (overrideText !== undefined ? overrideText : input).trim();
    if (!text) return;
    setError("");
    setInput("");
    setSending(true);
    const userTurn = { role: "user", content: text, ts: Date.now() };
    const nextHistory = [...messages, userTurn];
    setMessages(nextHistory);

    try {
      const r = await fetch(`${API}/api/content-calendar/drafts/${draft.id}/chat`, {
        method: "POST", headers: json,
        body: JSON.stringify({
          history: messages.map((m) => ({ role: m.role, content: m.content, artifact: m.artifact || null })),
          user_message: text,
          current_hook: hook,
          current_body: body,
          current_cta: cta,
          current_hashtags: (tags || "").split(/\s+/).filter(Boolean).map((t) => t.replace(/^#/, "")),
          pinned,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Chat turn failed");
      const aiTurn = {
        role: "ai",
        content: data.ai_message || "(no reply)",
        artifact: data.artifact || null,
        ts: Date.now(),
      };
      setMessages((prev) => [...prev, aiTurn]);
      if (data.artifact) setCandidate({ ...data.artifact, _msgIdx: nextHistory.length });
    } catch (e) {
      setError(e.message);
      setMessages((prev) => [...prev, { role: "ai", content: `(error: ${e.message})`, ts: Date.now() }]);
    } finally {
      setSending(false);
    }
  };

  /* ─── Accept the pending artifact → PATCH with revision log ───── */
  const acceptCandidate = async () => {
    if (!candidate) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/content-calendar/drafts/${draft.id}`, {
        method: "PATCH", headers: json,
        body: JSON.stringify({
          hook: candidate.hook,
          body: candidate.body,
          cta:  candidate.cta,
          hashtags: candidate.hashtags || [],
          revision_source: "ai-chat",
          revision_instruction: messages[(candidate._msgIdx ?? 1) - 1]?.content || "AI chat",
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Could not apply");
      // Mirror into the live editor state so the UI updates instantly.
      setHook(candidate.hook || "");
      setBody(candidate.body || "");
      setCta(candidate.cta || "");
      setTags((candidate.hashtags || []).join(" "));
      setCandidate(null);
      if (onApplied) onApplied(data.draft);
      loadRevisions();
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  /* ─── Discard the pending artifact ────────────────────────────── */
  const discardCandidate = () => setCandidate(null);

  /* ─── History tab ─────────────────────────────────────────────── */
  const loadRevisions = async () => {
    try {
      const r = await fetch(`${API}/api/content-calendar/drafts/${draft.id}/revisions`, {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const data = await r.json();
      if (r.ok) setRevisions(data.revisions || []);
    } catch (_) { /* noop */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadRevisions(); }, [draft.id]);

  const restoreRevision = async (revId) => {
    const ok = window.confirm("Restore this revision? The current copy will be saved to history first so you can undo if needed.");
    if (!ok) return;
    try {
      const r = await fetch(`${API}/api/content-calendar/drafts/${draft.id}/revisions/${revId}/restore`, {
        method: "POST", headers: json,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Restore failed");
      const d = data.draft || {};
      setHook(d.hook || "");
      setBody(d.body || "");
      setCta(d.cta || "");
      setTags((d.hashtags || []).join(" "));
      setPreviewRev(null);
      if (onApplied) onApplied(d);
      loadRevisions();
    } catch (e) { setError(e.message); }
  };

  /* ─── Context inspector ───────────────────────────────────────── */
  const loadContext = async () => {
    try {
      const r = await fetch(`${API}/api/content-calendar/drafts/${draft.id}/context`, {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const data = await r.json();
      if (r.ok) setContextDoc(data.context);
    } catch (_) { /* noop */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (contextOpen) loadContext(); }, [contextOpen, draft.id]);

  /* ─── Quick chips ─────────────────────────────────────────────── */
  const QUICK_CHIPS = [
    "Tighten the body — keep facts, cut filler.",
    "Make the post more advisory — concrete recommendation at the end.",
    "Swap the hook for a specific fact from the source.",
    "Add a stat from the source brief — no inventions.",
    "Stronger CTA — pull (no hard sell).",
    "Rewrite from scratch in a new angle.",
  ];

  return (
    <div data-testid={`cc-copilot-${draft.id}`}>
      {/* ── Tab bar ─────────────────────────────────────────────── */}
      <div style={{
        display: "flex", gap: 6, alignItems: "center",
        borderBottom: "1px solid rgba(12,42,64,0.10)",
        marginBottom: 14,
      }}>
        {[
          { k: "chat",    label: "Chat",    icon: <MessageSquare size={13} /> },
          { k: "history", label: `History (${revisions.length})`, icon: <History size={13} /> },
        ].map((t) => {
          const active = tab === t.k;
          return (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              data-testid={`cc-copilot-tab-${t.k}-${draft.id}`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "8px 14px",
                background: "transparent",
                border: "none",
                borderBottom: `2px solid ${active ? "#1FB6C1" : "transparent"}`,
                color: active ? "#0E7A82" : "rgba(12,42,64,0.55)",
                cursor: "pointer",
                fontSize: "0.82rem",
                fontWeight: active ? 700 : 500,
                marginBottom: -1,
              }}
            >
              {t.icon} {t.label}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        {/* Context inspector → info icon. No longer a tab. */}
        <button
          type="button"
          onClick={() => setContextOpen((v) => !v)}
          data-testid={`cc-copilot-context-toggle-${draft.id}`}
          title="What the AI sees on each turn"
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "6px 10px", marginBottom: 4,
            background: contextOpen ? "rgba(31,182,193,0.12)" : "transparent",
            border: `1px solid ${contextOpen ? "rgba(31,182,193,0.45)" : "rgba(12,42,64,0.14)"}`,
            color: contextOpen ? "#0E7A82" : "rgba(12,42,64,0.60)",
            borderRadius: 999, cursor: "pointer",
            fontSize: "0.7rem", fontFamily: "ui-monospace, monospace",
            letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600,
          }}
        >
          <Info size={11} /> Context
        </button>
      </div>

      {/* Context drawer — shown inline when the info icon is open. Keeps
          the transparency capability but removes the dedicated tab. */}
      {contextOpen && (
        <div data-testid={`cc-copilot-context-${draft.id}`}
             style={{
               marginBottom: 14, padding: 12,
               background: "#F8FBFB",
               border: "1px solid rgba(31,182,193,0.30)",
               borderRadius: 10,
             }}>
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono uppercase"
                 style={{ fontSize: "0.6rem", letterSpacing: "0.20em",
                          color: "#0E7A82", fontWeight: 700 }}>
              What the co-pilot sees
            </div>
            <button type="button" onClick={() => setContextOpen(false)}
                    style={{ background: "transparent", border: "none",
                             cursor: "pointer", color: "rgba(12,42,64,0.5)" }}>
              <X size={14} />
            </button>
          </div>
          {!contextDoc && <div style={{ padding: "6px 0", color: "rgba(12,42,64,0.5)", fontSize: "0.82rem" }}>Loading context…</div>}
          {contextDoc && (
            <div style={{ display: "grid", gap: 8 }}>
              <CtxBox label="Channel voice lane" value={contextDoc.channel_lane} />
              <CtxBox label="Source piece" value={`${contextDoc.source.title}\n${contextDoc.source.type} · ${contextDoc.source.slug}`} />
              <CtxBox label="Brand kit (loaded into prompt)"
                      value={`${contextDoc.brand_kit.name} — ${contextDoc.brand_kit.tagline}\nPrimary ${contextDoc.brand_kit.primary_color} · Accent ${contextDoc.brand_kit.secondary_color}${contextDoc.brand_kit.tone ? `\nTone: ${contextDoc.brand_kit.tone}` : ""}${contextDoc.brand_kit.logo_url ? `\nLogo: ${contextDoc.brand_kit.logo_url}` : "\nNo logo URL in brand kit yet."}`} />
              <CtxBox label="Revision history" value={`${contextDoc.revisions_count} prior snapshot(s) stored — visible in the History tab.`} />
            </div>
          )}
        </div>
      )}


      {error && (
        <div style={{
          background: "rgba(184,92,56,0.10)", border: "1px solid rgba(184,92,56,0.40)",
          color: "#B85C38", padding: "8px 12px", borderRadius: 8,
          fontSize: "0.82rem", marginBottom: 12,
        }} data-testid="cc-copilot-error">
          {error}
        </div>
      )}

      {/* ── CHAT TAB ────────────────────────────────────────────── */}
      {tab === "chat" && (
        <>
          {pinned.length > 0 && (
            <div style={{
              background: "#F3FBFB", border: "1px solid rgba(31,182,193,0.30)",
              borderRadius: 8, padding: "8px 12px", marginBottom: 10,
            }}>
              <div className="font-mono uppercase mb-1"
                   style={{ fontSize: "0.55rem", letterSpacing: "0.20em",
                            color: "#0E7A82", fontWeight: 700 }}>
                Pinned context
              </div>
              {pinned.map((p, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.82rem", color: "#0C2A40" }}>
                  <Pin size={11} />
                  <span style={{ flex: 1 }}>{p}</span>
                  <button onClick={() => setPinned((prev) => prev.filter((_, ix) => ix !== i))}
                          style={{ background: "transparent", border: "none", cursor: "pointer", color: "rgba(12,42,64,0.5)" }}>
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef}
               style={{ maxHeight: 360, overflowY: "auto", paddingRight: 4 }}
               data-testid={`cc-copilot-messages-${draft.id}`}>
            {messages.length === 0 && (
              <div style={{
                padding: "20px 16px",
                background: "#FCFCFA",
                border: "1px dashed rgba(12,42,64,0.18)",
                borderRadius: 8,
                color: "rgba(12,42,64,0.65)",
                fontSize: "0.85rem",
                lineHeight: 1.55,
              }}>
                Start a thread with the co-pilot. Ask for a full rewrite,
                a tone shift, a tighter body — anything. Every AI reply
                you accept is saved to History so you can roll back any
                time.
              </div>
            )}
            {messages.map((m, i) => (
              <ChatBubble key={i} m={m}
                          onAccept={m.artifact && i === messages.length - 1 ? () => { setCandidate({ ...m.artifact, _msgIdx: i }); } : null} />
            ))}
            {sending && (
              <div style={{ padding: "8px 12px", color: "rgba(12,42,64,0.5)", fontSize: "0.85rem", fontStyle: "italic" }}>
                Co-pilot is thinking…
              </div>
            )}
          </div>

          {/* Candidate preview (diff) — appears once an AI artifact is selected */}
          {candidate && (
            <div style={{
              marginTop: 14, padding: 14,
              background: "#FFFFFF",
              border: "1px solid rgba(31,182,193,0.45)",
              borderRadius: 10,
              boxShadow: "0 4px 16px -8px rgba(31,182,193,0.30)",
            }} data-testid={`cc-copilot-candidate-${draft.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div className="font-mono uppercase"
                     style={{ fontSize: "0.6rem", letterSpacing: "0.20em",
                              color: "#0E7A82", fontWeight: 700 }}>
                  Pending change — preview
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={discardCandidate} disabled={sending}
                          data-testid={`cc-copilot-discard-${draft.id}`}
                          style={{ padding: "6px 14px", background: "#FFFFFF", border: "1px solid rgba(12,42,64,0.18)",
                                   borderRadius: 999, fontSize: "0.78rem", color: "rgba(12,42,64,0.65)", cursor: "pointer", fontWeight: 500 }}>
                    Discard
                  </button>
                  <button onClick={acceptCandidate} disabled={sending}
                          data-testid={`cc-copilot-accept-${draft.id}`}
                          style={{ padding: "6px 14px", background: "#1FB6C1", border: "1px solid #1FB6C1",
                                   borderRadius: 999, fontSize: "0.78rem", color: "#FFFFFF", cursor: "pointer", fontWeight: 700 }}>
                    <Check size={11} style={{ display: "inline", marginRight: 4 }} />
                    Accept
                  </button>
                </div>
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {(hook !== candidate.hook) && <DiffBlock label="Hook" oldText={hook} newText={candidate.hook} testid={`cc-diff-hook-${draft.id}`} />}
                {(body !== candidate.body) && <DiffBlock label="Body" oldText={body} newText={candidate.body} testid={`cc-diff-body-${draft.id}`} />}
                {(cta  !== candidate.cta)  && <DiffBlock label="CTA"  oldText={cta}  newText={candidate.cta}  testid={`cc-diff-cta-${draft.id}`} />}
              </div>
            </div>
          )}

          {/* Quick chips */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12, marginBottom: 8 }}>
            {QUICK_CHIPS.map((c) => (
              <button key={c} type="button" disabled={sending}
                      onClick={() => send(c)}
                      data-testid={`cc-copilot-chip-${draft.id}`}
                      style={{
                        padding: "5px 10px", background: "#FFFFFF",
                        border: "1px solid rgba(12,42,64,0.14)",
                        borderRadius: 999, fontSize: "0.74rem",
                        color: "rgba(12,42,64,0.70)", cursor: "pointer", fontWeight: 500,
                      }}>
                {c}
              </button>
            ))}
          </div>

          {/* Composer */}
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 4 }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Talk to the co-pilot — e.g. rewrite from scratch, sharper hook, tighten the body, add a stat from the brief..."
              data-testid={`cc-copilot-input-${draft.id}`}
              rows={3}
              style={{
                flex: 1, padding: "10px 12px",
                background: "#FFFFFF",
                border: "1px solid rgba(12,42,64,0.16)",
                borderRadius: 8,
                fontSize: "0.88rem", color: "#0C2A40",
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <MicButton value={input} onChange={setInput} mode="append"
                         apiToken={apiToken}
                         testid={`cc-copilot-mic-${draft.id}`}
                         title="Dictate" />
              <button type="button" onClick={() => send()} disabled={sending || !input.trim()}
                      data-testid={`cc-copilot-send-${draft.id}`}
                      style={{
                        padding: "10px 14px",
                        background: input.trim() ? "#1FB6C1" : "rgba(31,182,193,0.30)",
                        border: "none", borderRadius: 8,
                        color: "#FFFFFF", cursor: input.trim() ? "pointer" : "not-allowed",
                        fontWeight: 700, fontSize: "0.85rem",
                        display: "inline-flex", alignItems: "center", gap: 6,
                      }}>
                <Send size={13} /> Send
              </button>
            </div>
          </div>

          {/* Add-pinned input */}
          <div style={{ marginTop: 10, display: "flex", gap: 6 }}>
            <input
              type="text"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && pinInput.trim()) {
                  setPinned((prev) => [...prev, pinInput.trim()]);
                  setPinInput("");
                }
              }}
              placeholder="Pin a fact or constraint for the whole thread… (enter to add)"
              style={{
                flex: 1, padding: "6px 10px",
                background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.12)",
                borderRadius: 6, fontSize: "0.78rem", color: "#0C2A40",
              }}
            />
          </div>
        </>
      )}

      {/* ── HISTORY TAB ─────────────────────────────────────────── */}
      {tab === "history" && (
        <div data-testid={`cc-copilot-history-${draft.id}`}>
          {revisions.length === 0 && (
            <div style={{
              padding: "20px 16px", background: "#FCFCFA",
              border: "1px dashed rgba(12,42,64,0.18)", borderRadius: 8,
              color: "rgba(12,42,64,0.65)", fontSize: "0.85rem",
            }}>
              No history yet. Every edit you save — manual, AI refine, or
              co-pilot chat — appears here. You can restore any version
              with one click, and the restore itself is undoable.
            </div>
          )}
          {revisions.map((r) => (
            <div key={r.id}
                 style={{
                   padding: "10px 12px", marginBottom: 8,
                   background: previewRev === r.id ? "#F3FBFB" : "#FFFFFF",
                   border: `1px solid ${previewRev === r.id ? "rgba(31,182,193,0.45)" : "rgba(12,42,64,0.10)"}`,
                   borderRadius: 8,
                 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div>
                  <div style={{ fontSize: "0.74rem", color: "rgba(12,42,64,0.55)", fontFamily: "ui-monospace, monospace" }}>
                    {new Date(r.ts).toLocaleString()} · {r.source}
                  </div>
                  {r.instruction && (
                    <div style={{ fontSize: "0.82rem", color: "#0C2A40", marginTop: 2, lineHeight: 1.45 }}>
                      {r.instruction.slice(0, 140)}{r.instruction.length > 140 ? "…" : ""}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button onClick={() => setPreviewRev(previewRev === r.id ? null : r.id)}
                          style={{ padding: "4px 10px", background: "#FFFFFF", border: "1px solid rgba(12,42,64,0.16)",
                                   borderRadius: 999, fontSize: "0.72rem", color: "rgba(12,42,64,0.70)", cursor: "pointer" }}>
                    {previewRev === r.id ? "Hide" : "Preview"}
                  </button>
                  <button onClick={() => restoreRevision(r.id)}
                          data-testid={`cc-revision-restore-${r.id}`}
                          style={{ padding: "4px 10px", background: "#1FB6C1", border: "1px solid #1FB6C1",
                                   borderRadius: 999, fontSize: "0.72rem", color: "#FFFFFF", cursor: "pointer", fontWeight: 700,
                                   display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <RotateCcw size={10} /> Restore
                  </button>
                </div>
              </div>
              {previewRev === r.id && (
                <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                  <DiffBlock label="Hook (then → now)" oldText={r.hook} newText={hook} />
                  <DiffBlock label="Body (then → now)" oldText={r.body} newText={body} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChatBubble({ m, onAccept }) {
  const isUser = m.role === "user";
  return (
    <div style={{
      display: "flex", justifyContent: isUser ? "flex-end" : "flex-start",
      marginBottom: 10,
    }}>
      <div style={{
        maxWidth: "85%",
        padding: "8px 12px",
        background: isUser ? "#0C2A40" : "#F3FBFB",
        color: isUser ? "#FAFAF7" : "#0C2A40",
        border: isUser ? "none" : "1px solid rgba(31,182,193,0.25)",
        borderRadius: 12,
        borderTopRightRadius: isUser ? 4 : 12,
        borderTopLeftRadius:  isUser ? 12 : 4,
        fontSize: "0.86rem", lineHeight: 1.5,
        whiteSpace: "pre-wrap",
      }}>
        {m.content}
        {!isUser && m.artifact && onAccept && (
          <button onClick={onAccept}
                  style={{
                    marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "5px 12px", background: "#1FB6C1", color: "#FFFFFF",
                    border: "none", borderRadius: 999, fontSize: "0.74rem", fontWeight: 700,
                    cursor: "pointer",
                  }}>
            <Check size={11} /> Review candidate
          </button>
        )}
      </div>
    </div>
  );
}

function CtxBox({ label, value }) {
  return (
    <div style={{ padding: "10px 12px", background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.10)", borderRadius: 8 }}>
      <div className="font-mono uppercase mb-1"
           style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                    color: "rgba(12,42,64,0.55)", fontWeight: 700 }}>
        {label}
      </div>
      <div style={{ fontSize: "0.82rem", color: "#0C2A40", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
        {value}
      </div>
    </div>
  );
}
